// ModelVariableNodeView.tsx
import { ComponentProps, FC } from "react";
import { NodeViewWrapper, NodeViewProps, NodeViewContent } from "@tiptap/react";
import { useRichTextModelQuantities } from "@/Pages/DesignStudio/Display/rich-text/useRichTextModelQuantities";
import { ModelQuantityInlineButton } from "@/components/rich-text/ModelQuantity";
import {
    Popover,
    PopoverTrigger,
    PopoverContent,
} from "@/components/popover/Popover";
import { NodeSelection, TextSelection } from "prosemirror-state";
import { Editor } from "@tiptap/core";
import { Pencil, SlidersHorizontal } from "lucide-react";
import { VariableIcon } from "@heroicons/react/24/outline";
import { signForValue } from "./signForValue";
import { applySignGlyph } from "./applySignGlyph";
import { useHighlightModelValues } from "@/hooks/useHighlightModelValues";
import { useIsInDesignLayerContext } from "@/hooks/useIsInDesignLayerContext";

function focusModelVariableById(
    editor: Editor,
    richTextModelQuantityId: string,
) {
    const selector = `[data-rich-text-model-quantity-id="${richTextModelQuantityId}"]`;
    const dom = editor.view.dom.querySelector(selector);
    if (!dom) return false;

    const pos = editor.view.posAtDOM(dom, 0); // 0 = before the node
    if (pos === null || pos === undefined) return false;

    const tr = editor.state.tr.setSelection(
        NodeSelection.create(editor.state.doc, pos),
    );
    editor.view.dispatch(tr);
    editor.view.focus();
    return true;
}

const ModelVariableNodeView: FC<NodeViewProps> = ({
    node,
    editor,
    updateAttributes,
}) => {
    const { modelValues: values } = useRichTextModelQuantities();
    const { highlightModelValues } = useHighlightModelValues();
    const { isInDesignLayerContext } = useIsInDesignLayerContext();

    const richTextModelQuantityId = node.attrs["rich-text-model-quantity-id"];
    const colorBySign = !!node.attrs["color-by-sign"];
    const useSignGlyph = !!node.attrs["use-sign-glyph"];
    const positiveGlyph = (node.attrs["positive-glyph"] ?? "▲") as string;
    const negativeGlyph = (node.attrs["negative-glyph"] ?? "▼") as string;
    const suffix = node.attrs["suffix"] as string | null;
    const key = richTextModelQuantityId;

    const value = values?.[key]?.value || "Loading...";
    const { modelVariableId, timeHorizonId, label, timeIndex, numericalValue } =
        values?.[key] || {};

    // Opt-in coloring only. Zero is neutral (no data-sign), so it inherits
    // the default text color. CSS keys off [data-sign] — see app.css.
    const sign = signForValue(numericalValue, colorBySign);

    // Opt-in glyph: replace the value's +/- sign with a chosen symbol.
    const displayValue = applySignGlyph(
        value,
        numericalValue,
        useSignGlyph,
        positiveGlyph,
        negativeGlyph,
    );

    // Swapping the underlying variable rewrites the node in place. Re-append
    // the suffix so the mark→node conversion can re-extract it; otherwise
    // changing the variable would silently drop the label.
    const handleVariableChange: ComponentProps<
        typeof ModelQuantityInlineButton
    >["onChange"] = ({ richTextModelQuantity }) => {
        if (!focusModelVariableById(editor, richTextModelQuantityId)) {
            console.log("failed to focus");
            return;
        }

        const { state, view } = editor;
        const { tr, schema } = state;
        const { selection } = state;

        const $pos = state.doc.resolve(selection.from);
        const node = $pos.nodeAfter ?? $pos.nodeBefore;

        if (!node || node.type.name !== "modelVariable") {
            console.log("not a model variable", node);
            state.doc.nodesBetween(selection.from, selection.to, (n, pos) => {
                console.log(pos, n.type.name, n.attrs);
            });
            return;
        }
        const from = $pos.pos;
        const to = from + node.nodeSize;

        const mark = schema.marks.dataAttributes.create({
            "rich-text-model-quantity-id": richTextModelQuantity.id,
        });
        const textValue = suffix ? `${value} ${suffix}` : value;
        const textNode = schema.text(textValue, [mark]);

        // ✅ Replace the entire node
        tr.replaceWith(from, to, textNode);

        // Re-select the text for UX if needed
        tr.setSelection(
            TextSelection.create(tr.doc, from, from + textValue.length),
        );

        view.dispatch(tr);
        view.focus();
    };

    return (
        <NodeViewWrapper
            as="span"
            data-rich-text-model-quantity-id={richTextModelQuantityId}
            data-model-variable-id={modelVariableId || undefined}
            data-time-horizon-id={timeHorizonId || undefined}
            data-model-variable-name={label || undefined}
            data-time-index={timeIndex || undefined}
            data-is-editable={editor.isEditable ? "true" : undefined}
            className="group/inline-variable inline-variable relative inline align-baseline"
        >
            {/* All editor chrome lives in a hover-opened popover so nothing
                renders inline beside the value (which used to break layouts). */}
            {editor.isEditable && (
                <Popover>
                    <PopoverTrigger asChild>
                        <button
                            type="button"
                            title="Edit model value display"
                            contentEditable={false}
                            onMouseDown={(e) => e.preventDefault()}
                            className="absolute -right-2 -top-2 z-10 rounded-full bg-white p-0.5 text-gray-600
                                opacity-0 shadow transition-opacity hover:text-gray-900
                                group-hover/inline-variable:opacity-100"
                        >
                            <SlidersHorizontal className="h-3 w-3" />
                        </button>
                    </PopoverTrigger>
                    <PopoverContent
                        align="start"
                        className="w-64 border border-gray-200 bg-white p-3 text-gray-800"
                        onOpenAutoFocus={(e) => e.preventDefault()}
                    >
                        <div
                            contentEditable={false}
                            className="flex flex-col gap-3 text-sm text-gray-800"
                        >
                            {!!label && (
                                <div className="flex flex-col">
                                    <span className="font-semibold">
                                        {label}
                                    </span>
                                    {timeIndex !== null &&
                                        timeIndex !== undefined && (
                                            <span className="text-xs text-gray-500">
                                                Time horizon {timeIndex}
                                            </span>
                                        )}
                                </div>
                            )}

                            <ModelQuantityInlineButton
                                modelVariableId={modelVariableId}
                                timeHorizonId={timeHorizonId}
                                richTextModelQuantityId={
                                    richTextModelQuantityId
                                }
                                onChange={handleVariableChange}
                                triggerClassName="inline-flex w-fit items-center gap-1 rounded border
                                    border-gray-300 px-2 py-1 text-xs hover:bg-gray-50"
                                triggerContent={
                                    <>
                                        <Pencil className="h-3 w-3" />
                                        Change variable
                                    </>
                                }
                            />

                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={colorBySign}
                                    onChange={(e) =>
                                        updateAttributes({
                                            "color-by-sign": e.target.checked,
                                        })
                                    }
                                />
                                <span>Color by positive / negative</span>
                            </label>

                            <div className="flex flex-col gap-2">
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={useSignGlyph}
                                        onChange={(e) =>
                                            updateAttributes({
                                                "use-sign-glyph":
                                                    e.target.checked,
                                            })
                                        }
                                    />
                                    <span>Replace +/− sign with symbols</span>
                                </label>
                                {useSignGlyph && (
                                    <div className="flex gap-3 pl-6">
                                        <label className="flex flex-col gap-1 text-xs text-gray-500">
                                            Positive
                                            <input
                                                type="text"
                                                maxLength={4}
                                                value={positiveGlyph}
                                                onChange={(e) =>
                                                    updateAttributes({
                                                        "positive-glyph":
                                                            e.target.value,
                                                    })
                                                }
                                                className="w-12 rounded border border-gray-300 px-1 py-1
                                                    text-center text-base text-gray-900"
                                            />
                                        </label>
                                        <label className="flex flex-col gap-1 text-xs text-gray-500">
                                            Negative
                                            <input
                                                type="text"
                                                maxLength={4}
                                                value={negativeGlyph}
                                                onChange={(e) =>
                                                    updateAttributes({
                                                        "negative-glyph":
                                                            e.target.value,
                                                    })
                                                }
                                                className="w-12 rounded border border-gray-300 px-1 py-1
                                                    text-center text-base text-gray-900"
                                            />
                                        </label>
                                    </div>
                                )}
                            </div>
                        </div>
                    </PopoverContent>
                </Popover>
            )}
            {/* Stopgap author affordance (editor-only, toggle-gated): a blue
                variable glyph prepended to each value so model quantities are
                easy to spot in prose. Sized to 1em so it tracks the
                surrounding text; aria-hidden + contentEditable=false so it's
                neither read out nor editable. */}
            {highlightModelValues && isInDesignLayerContext && (
                <VariableIcon
                    aria-hidden="true"
                    className="mr-px inline h-[1em] w-[1em] shrink-0 align-[-0.125em] text-blue-600"
                />
            )}
            <NodeViewContent as="span" data-sign={sign}>
                {displayValue}
                {!!suffix && (
                    <span className="model-quantity-suffix ml-1">{suffix}</span>
                )}
            </NodeViewContent>
        </NodeViewWrapper>
    );
};

export default ModelVariableNodeView;
